import React, { createContext, useReducer, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

// redux
import { getSearchParams } from "frontend/store/browse";
import { getSearchMode } from "frontend/store/layout";

// context
export const SearchBarContext = createContext();
SearchBarContext.displayName = "SearchBar"; // Display Name
import { reducer } from "./context/store";

// helpers
import { getDefaultProps } from "./context/getDefaultProps";
import { convertSearchModeToType } from "./context/convertSearchModeToType";

// hooks
import { useViewport } from "js/hooks/useViewport";

// components
import MountingWrapper from "./components/MountingWrapper";
import Form from "./components/Form";

// styles
import colors, { focusColor } from "core/colors";
import { convertPxToRem } from "frontend/components/helpers";

export const getBorderStyles = isDarkMode => {
    const { primary, secondary } = colors;

    const width = convertPxToRem(3);
    const color = isDarkMode ? secondary.dolphin : primary.diamond;
    const hoverColor = isDarkMode ? secondary.novemberRain : secondary.oolong;

    return {
        width,
        color,
        hoverColor,
        focusColor,
        border: `solid ${width} ${color}`,
        hoverBorder: `solid ${width} ${hoverColor}`,
        focusBorder: `solid ${width} ${focusColor}`
    };
};

// component
export const SearchBar = ({
    id,
    className,
    children,
    isDarkMode = false,
    clearButtonScale = 1.25,
    isDisabled = false,
    searchInputRef = null,
    inputOnFocus = () => {},
    inputOnBlur = () => {}
}) => {
    const viewport = useViewport();
    const searchParams = useSelector(getSearchParams);
    const searchMode = convertSearchModeToType(useSelector(getSearchMode));

    const { dropDown, inputValue } = getDefaultProps({
        searchMode,
        searchParams
    });

    const nonStateProps = {
        id,
        viewport,
        isDarkMode,
        clearButtonScale,
        searchMode,
        isDisabled,
        inputOnFocus,
        inputOnBlur,
        searchInputRef
    };
    const stateProps = {
        dropDown,
        inputValue
    };

    // Whatever you want to manage as internal state
    const [state, dispatch] = useReducer(reducer, stateProps);

    // useMemo so it does not pass value on every render
    const value = useMemo(() => ({ state, dispatch, nonStateProps }), [
        state,
        dispatch,
        nonStateProps
    ]);

    return (
        <SearchBarContext.Provider value={value}>
            <MountingWrapper>
                <Form className={className}>{children}</Form>
            </MountingWrapper>
        </SearchBarContext.Provider>
    );
};

export default SearchBar;

// prop-types
const inputElementInstance =
    typeof HTMLInputElement !== "undefined" ? HTMLInputElement : () => {};

SearchBar.propTypes = {
    children: PropTypes.any,
    isDarkMode: PropTypes.bool,
    id: PropTypes.string.isRequired,
    className: PropTypes.string,
    clearButtonScale: PropTypes.number,
    isDisabled: PropTypes.bool,
    inputOnFocus: PropTypes.func,
    inputOnBlur: PropTypes.func,
    searchInputRef: PropTypes.shape({
        current: PropTypes.instanceOf(inputElementInstance)
    })
};
